import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Empty, Segmented } from 'antd';
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useListContext } from 'react-admin';
import {
    ColumnHeader,
    FilterButton,
    FilterField,
    FilterToolbar,
    HeaderColumn,
    InfiniteFoot,
    isFieldActive,
    useHashAccordion,
    useHeaderShade,
    useInfinitePages,
} from '../DataList';
import MainCard from '../MainCard';
// import { OpenRowHint } from '../OpenCard';
import { NetOrcaTableListV1Props } from './types';
import './NetOrcaTableListV1.css';

const SKELETON_COUNT = 6;

/**
 * The standard list: a row per record under one header, where every column
 * header is also that column's filter, and pages arrive as the reader scrolls.
 *
 * Filters live in exactly one place at a time. A column header holds its own,
 * and the panel behind the Filters button holds the rest — until the card view
 * takes the headers away, when the panel takes every filter back. Nothing is
 * ever offered twice, and nothing is ever out of reach.
 *
 * Reads its records from react-admin's list context, so it goes inside a
 * ListBase. A `source` overrides them for a page that swaps endpoints under the
 * same filters.
 *
 * Rows are the caller's: pass `renderRow`, and `renderCard` too if the list is
 * worth seeing as tiles — that is what puts the view switch on the bar. Give
 * the header a `template` matching the rows' columns, ideally the same CSS
 * variable, and they cannot drift apart.
 *
 * Rows that expand in place ask for `accordion`, which opens one at a time from
 * the URL hash and hands each row its state through renderRow's second
 * argument. A link to a row the list has not loaded yet is followed: pages keep
 * coming until it turns up.
 */
const NetOrcaTableListV1 = ({
    fields,
    columns,
    template,
    renderRow,
    renderCard,
    accordion,
    renderWrapper,
    banner,
    scrollBanner = false,
    defaultView = 'list',
    source,
    headerTrailing,
    filterButtonLabel,
    drawerTitle,
    drawerDescription,
    drawerFooter,
    toolbarExtra,
    emptyText = 'Nothing found',
    noun = 'item',
    plural,
    className,
    dataCy = 'table-list-results',
}: NetOrcaTableListV1Props) => {
    const {
        ids, data, loading, currentSort,
        filterValues, setFilters, page, setPage, total,
    } = useListContext();

    const [view, setView] = useState<'grid' | 'list'>(renderCard ? defaultView : 'list');
    const [filtersOpen, setFiltersOpen] = useState(false);

    // What the list filters by before anyone touches it. A header names its
    // column until the reader narrows it themselves, so a list that opens with
    // a default state does not open with that state where its title should be.
    const openedWith = useRef(filterValues);

    // How tall the sticky header is, as --ntl-sticky-h on the list's root: a
    // row brought to the top of the page stops below it (scroll-margin-top in
    // the stylesheet) instead of under it. Measured, because the filters wrap
    // onto more lines as the page narrows.
    const rootRef = useRef<HTMLDivElement>(null);
    const [dock, setDock] = useState<HTMLDivElement | null>(null);
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        const measure = () => root.style.setProperty('--ntl-sticky-h', `${dock ? dock.offsetHeight : 0}px`);
        measure();
        if (!dock || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(dock);
        return () => observer.disconnect();
    }, [dock]);

    // the header's shadow as rows scroll under it, and its corners once stuck
    const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
    useHeaderShade(dock, scroller, 'ntl-head-dock');

    const ownRecords = useMemo(() => {
        const records: any[] = [];
        ids.forEach((id) => { if (data[id]) records.push(data[id]); });
        return records;
    }, [ids, data]);

    const pageRecords = source ? source.records : ownRecords;
    const isLoading = source ? !!source.loading : loading;
    const knownTotal = (source ? source.total : total) ?? 0;

    /* ---- endless scroll ---- */

    // Everything that changes what a page means starts the list over. Key order
    // in filterValues is not guaranteed between renders, and a spurious change
    // here would throw away everything already loaded.
    const queryKey = JSON.stringify([
        Object.keys(filterValues).sort().map((key) => [key, filterValues[key]]),
        currentSort?.field, currentSort?.order, source?.key ?? '',
    ]);

    // one row open at a time, named in the URL hash, where the page asked for it
    const hash = useHashAccordion({ aliasOf: accordion?.aliasOf });

    const { items, hasMore, sentinelRef, settling } = useInfinitePages({
        queryKey,
        records: pageRecords,
        loading: isLoading,
        total: knownTotal,
        page,
        setPage,
    });

    const rowState = (record: any) => ({
        open: hash.isOpen(record),
        toggle: () => hash.toggle(record),
        scrollIntoView: hash.scrollsTo(record),
    });

    /* ---- filters: on the headers, or in the panel ---- */

    // The header only exists over rows, so the card view has none — there, every
    // filter goes back to the panel. Where the header is up it owns its filters
    // outright; either way they stay in the schema, so their values survive the
    // switch and "Clear all" still reaches them.
    const headerVisible = view === 'list';
    const fronted = useMemo(
        () => (headerVisible
            ? columns.reduce<string[]>((names, column) => names.concat(column.filters ?? []), [])
            : []),
        [headerVisible, columns],
    );

    /** the whole schema, each field marked with where it is drawn */
    const schema: FilterField[] = useMemo(
        () => fields.map((field) => (
            fronted.includes(field.name) ? { ...field, renderedElsewhere: true } : field
        )),
        [fields, fronted],
    );

    /** what the panel actually holds — the rest are on the headers */
    const panelFields = useMemo(
        () => schema.filter((field) => !field.renderedElsewhere && !field.hidden),
        [schema],
    );

    const headerColumns: HeaderColumn[] = useMemo(
        () => columns.map((column) => ({
            label: column.label,
            icon: column.icon,
            align: column.align,
            dataCy: column.dataCy,
            fields: schema.filter((field) => (column.filters ?? []).includes(field.name)),
        })),
        [columns, schema],
    );

    /* ---- render ---- */

    // The view switch sits in the panel with the other things that shape the
    // list, rather than taking a bar of its own above it.
    const viewField = renderCard ? (
        <div className="ft-field">
            <span className="ft-field__label">View</span>
            <Segmented
                className="ntl-view"
                data-cy="view-switch"
                block
                value={view}
                onChange={(value) => setView(value as 'grid' | 'list')}
                options={[
                    { value: 'list', icon: <UnorderedListOutlined />, label: 'Rows' },
                    { value: 'grid', icon: <AppstoreOutlined />, label: 'Cards' },
                ]}
            />
        </div>
    ) : null;

    // A panel with nothing in it is not worth a button: where every filter is on
    // a header and there is nothing else to set, none is offered.
    const panelEmpty = panelFields.length === 0 && !drawerFooter && !viewField;
    // only what the panel holds: a filter set on its own column header says so
    // there, and counting it here would say it twice
    const activeInPanel = panelFields.filter((field) => isFieldActive(field, filterValues)).length;

    // In the header the trigger takes a column's worth of room, so it goes
    // compact; without a header it leads the bar as usual.
    const headerTrigger = headerVisible && !panelEmpty
        ? <FilterButton compact count={activeInPanel} onClick={() => setFiltersOpen(true)} />
        : null;

    const trailing = (headerTrailing || headerTrigger)
        ? <span className="ntl-head__end">{headerTrailing}{headerTrigger}</span>
        : undefined;

    const renderResults = () => {
        // Only the first page shows skeletons; later pages load under the list.
        // `settling` counts as loading: the query just changed, so an empty list
        // means the answer has not arrived, not that there is none.
        if ((isLoading || settling) && items.length === 0) {
            return (
                <div className={view === 'grid' ? 'ntl-grid' : 'ntl-list'}>
                    {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <div key={i} className={view === 'grid' ? 'ntl-skel ntl-skel--card' : 'ntl-skel'} />
                    ))}
                </div>
            );
        }
        if (items.length === 0) {
            return <Empty className="ntl-empty" description={emptyText} />;
        }
        // `each` rather than `stagger`: this list grows under the reader as
        // pages load, so only the newly appended rows mount and animate. A
        // stagger delay would make every new batch hang before appearing.
        return view === 'grid' ? (
            <div className="ntl-grid nx-enter-each">
                {items.map((record) => renderCard!(record, rowState(record)))}
            </div>
        ) : (
            <div className="ntl-list nx-enter-each">
                {items.map((record) => renderRow(record, rowState(record)))}
            </div>
        );
    };

    // the header, in its dock — over the rows, or among them as the first
    // thing after a banner that scrolls (scrollBanner)
    const headerDock = headerVisible && (
        // the dock sticks and covers; the header keeps its own look
        <div ref={setDock} className="ntl-head-dock">
            <ColumnHeader
                className="ntl-head"
                template={template}
                columns={headerColumns}
                schema={schema}
                values={filterValues}
                defaults={openedWith.current}
                onChange={(next) => setFilters(next, {})}
                trailing={trailing}
            />
        </div>
    );

    return (
        <div ref={rootRef} className={`ntl netorca-frame${className ? ` ${className}` : ''}`}>
            <MainCard className="ntl-panel">
                <div className="ntl-panel__body">
                    {/* whatever the page shows above its list — its tabs, say —
                        inside the same frame rather than in one of its own;
                        a banner that scrolls goes in with the rows instead */}
                    {!scrollBanner && banner}
                    <FilterToolbar
                        className="ntl-toolbar"
                        layout="drawer"
                        filterButtonLabel={filterButtonLabel}
                        // the header holds the trigger while it is up; the bar
                        // draws nothing unless something is active
                        hideTrigger={!!headerTrigger || panelEmpty}
                        open={filtersOpen}
                        onOpenChange={setFiltersOpen}
                        drawerTitle={drawerTitle}
                        // no standing explanation: the panel's contents say what
                        // they are, and a paragraph above them only pushes them
                        // down the page
                        drawerDescription={drawerDescription}
                        drawerFooter={(viewField || drawerFooter) ? <>{viewField}{drawerFooter}</> : undefined}
                        fields={schema}
                        values={filterValues}
                        onChange={(next) => setFilters(next, {})}
                        // react-admin keeps filters per resource, so a key set by a
                        // page with a different filter set arrives here and is sent
                        // verbatim; backends that validate strictly reject the lot
                        pruneUnknownValues
                        extra={toolbarExtra}
                    />

                    <div className="ntl-results" data-cy={dataCy}>
                        {/* stays put while loading or empty, so a filter that
                            returned nothing can be undone from it */}
                        {!scrollBanner && headerDock}
                        {/* The rows and the count under them. Where the list is
                            the page, this is what scrolls, inside a card that
                            stays put; inside another page it is plain, and that
                            page scrolls — see .ntl-scroll in the stylesheet. */}
                        <div ref={setScroller} className="ntl-scroll">
                            {/* a banner that scrolls, and the header after it,
                                sticking at the top once the banner has gone */}
                            {scrollBanner && banner}
                            {scrollBanner && headerDock}
                            {/* a row open in place (useExpandedRow) gets a circle
                                over the row before it and one over the row after,
                                each closing it — inert while nothing is open.
                                Off now: the list scrolls freely around an open row. OpenRowHint stays, to bring back. */}
                            {/* <OpenRowHint edge="top" noun={noun} /> */}
                            {renderWrapper ? renderWrapper(renderResults()) : renderResults()}

                            {/* <OpenRowHint edge="bottom" noun={noun} /> */}

                            {/* pages load as this comes into view — no page numbers */}
                            <InfiniteFoot
                                sentinelRef={sentinelRef}
                                loading={isLoading}
                                loaded={items.length}
                                total={knownTotal}
                                hasMore={hasMore}
                                noun={noun}
                                plural={plural}
                            />
                        </div>
                    </div>
                </div>
            </MainCard>
        </div>
    );
};

export default NetOrcaTableListV1;
